import React from "react";
import moment from "moment";
import { DV2 } from "../DeclarationV2/theme";
import { euros, hoursFromHalfHours, monthName, plural } from "../DeclarationV2/format";
import { vocab } from "../DeclarationV2/vocabulary";
import { isVerifiedTrainer } from "../../Utils/VerifiedTrainerAmounts";
import { useDv2Theme } from "../DeclarationV2/layout/Ui";
import { PastPeriodStats } from "./usePastPeriodStats";

/**
 * Les trois totaux du mois en tête de l'onglet Passés (maquette 6c) : reçu,
 * en attente de validation, refusés ou contestés. Ils remplacent le bouton
 * « Total facturé » de la liste actuelle.
 */
export default function PastCoursesTiles({ stats, period }: { stats: PastPeriodStats; period: string }) {
    const { primary, fontFamily, text } = useDv2Theme();
    const month = monthName(moment(period, "YYYY-MM"));
    const dash = stats.loading ? "…" : "-";

    const Tile = ({ label, value, color, sub }: { label: string; value: string; color?: string; sub: string }) => (
        <div style={{ background: DV2.surface, border: `1px solid ${DV2.border}`, borderRadius: 16, padding: "16px 20px", fontFamily, color: text }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: DV2.muted }}>{label}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: color ?? text, lineHeight: 1.2, marginTop: 2 }}>{value}</div>
            <div style={{ fontSize: 12, color: DV2.muted, marginTop: 2 }}>{sub}</div>
        </div>
    );

    const receivedSub = stats.received == null
        ? (stats.loading ? "Calcul en cours" : "Montant indisponible")
        : [stats.halfHours != null ? hoursFromHalfHours(stats.halfHours) : null, stats.clientCount != null ? plural(stats.clientCount, vocab.client(), vocab.clients()) : null].filter(Boolean).join(", ") || "Validés, prélevés et collectés";

    const totalSub = (t: PastPeriodStats["pending"], none: string, detail?: string) => {
        if (!t) return stats.loading ? "Calcul en cours" : none;
        if (t.count === 0) return none;
        return `${plural(t.count, vocab.course(), vocab.courses())}${t.complete ? "" : " au moins"}${detail ? `, ${detail}` : ""}`;
    };

    // Détail sous « En attente » : le délai client ne vaut que pour les demandes déjà transmises.
    const pendingDetail = (t: PastPeriodStats["pending"]): string => {
        if (!t) return "";
        const parts = [
            t.notTransmitted ? `${t.notTransmitted} pas encore transmis${vocab.course() === "prestation" ? (t.notTransmitted > 1 ? "es" : "e") : ""}` : null,
            t.notDeclared ? `${t.notDeclared} en revue par notre équipe` : null,
        ].filter(Boolean);
        return parts.length > 0 ? `dont ${parts.join(", ")}` : "le client a 48 h";
    };

    return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" aria-label={`Totaux de ${month}`}>
            <Tile
                label={isVerifiedTrainer() ? `Prix total en ${month}` : `Reçu en ${month}`}
                value={stats.received == null ? dash : euros(stats.received)}
                color={primary}
                sub={receivedSub}
            />
            <Tile
                label="En attente de validation"
                value={stats.pending ? euros(stats.pending.amount) : dash}
                color={stats.pending && stats.pending.count > 0 ? DV2.warning : undefined}
                sub={totalSub(stats.pending, `aucun ${vocab.course()} en attente`, pendingDetail(stats.pending))}
            />
            <Tile
                label="Refusés ou contestés"
                value={stats.refused ? euros(stats.refused.amount) : dash}
                color={stats.refused && stats.refused.count > 0 ? DV2.danger : undefined}
                sub={totalSub(stats.refused, "aucun ce mois", stats.refused?.notDeclared ? `dont ${stats.refused.notDeclared} justificatif${stats.refused.notDeclared > 1 ? "s" : ""} à renvoyer` : undefined)}
            />
        </div>
    );
}
